import type {
  VisitorProfile,
  VisitorProfileField,
  VisitorProfileFieldUpdate,
} from "@/lib/graphql/queries/generated/profile.generated";

/** Seed local edit state from the BE-resolved profile. */
export function seedValues(p: VisitorProfile): Record<string, string | null> {
  const out: Record<string, string | null> = {};
  for (const f of p.fields) out[f.key] = f.value ?? null;
  return out;
}

export function isEmpty(v: string | null): boolean {
  return v == null || v === "" || v.trim() === "";
}

/** Canonical wire-form for a value of a given type — used both when
 *  writing the input AND when computing the diff so the two stay in
 *  sync. `value` is always string-serialised on the wire. */
export function normalise(value: string | null, type: VisitorProfileField["type"]): string | null {
  if (value == null) return null;
  switch (type) {
    case "STRING":
    case "ENUM":
    case "AVATAR":
      return value.trim() === "" ? null : value.trim();
    case "NUMBER": {
      const trimmed = value.trim();
      if (trimmed === "") return null;
      const n = Number(trimmed);
      return Number.isFinite(n) ? String(n) : trimmed;
    }
    case "BOOLEAN":
      return value === "true" ? "true" : "false";
    case "DATE":
      return value === "" ? null : value;
    case "ARRAY": {
      const trimmed = value.trim();
      if (trimmed === "") return null;
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return JSON.stringify(parsed.map(String));
      } catch {
        /* fall through — treat as comma-separated literal */
      }
      return JSON.stringify(
        trimmed
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      );
    }
    default:
      return value;
  }
}

/** Diff visitor's edits against the BE-resolved baseline — returns ONLY
 *  the changed fields, in `{key, value}[]` shape the mutation expects.
 *  Pinned rows are filtered out so the BE never sees a write attempt
 *  for a locked field (it would reject anyway with `webProfileFieldPinned`). */
export function diffFields(
  fields: VisitorProfileField[],
  values: Record<string, string | null>,
): VisitorProfileFieldUpdate[] {
  const updates: VisitorProfileFieldUpdate[] = [];
  for (const f of fields) {
    if (f.pinned) continue;
    const base = normalise(f.value ?? null, f.type);
    const next = normalise(values[f.key] ?? null, f.type);
    if (base === next) continue;
    updates.push({ key: f.key, value: next ?? "" });
  }
  return updates;
}

/** Friendly display name for the avatar hero: prefer the explicit
 *  `firstName` + `lastName` pair, then any `name`-ish single field,
 *  then fall back to nothing. */
export function pickDisplayName(p: VisitorProfile, vals: Record<string, string | null>): string {
  const grab = (key: string) =>
    vals[key]?.trim() || p.fields.find((f) => f.key === key)?.value?.trim() || "";
  const first = grab("firstName");
  const last = grab("lastName");
  if (first || last) return `${first} ${last}`.trim();
  return grab("name") || grab("displayName") || "";
}

/** Coerce any BE-supplied date string (`2024-01-15` or full ISO) into
 *  the `YYYY-MM-DD` shape `<input type="date">` requires. */
export function dateInputValue(v: string | null): string {
  if (!v) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

/** Parse an ARRAY field value (JSON-array on the wire) into a list of
 *  strings; returns `[]` for anything malformed. */
export function parseArrayValue(v: string | null): string[] {
  if (!v) return [];
  try {
    const parsed = JSON.parse(v);
    if (Array.isArray(parsed)) return parsed.map((x) => String(x));
  } catch {
    /* fall through */
  }
  return [];
}

/** Title-case a locale display name returned by `Intl.DisplayNames`. */
export function localeDisplayName(code: string, in_: string): string {
  try {
    const dn = new Intl.DisplayNames([in_], { type: "language" });
    const out = dn.of(code);
    if (out) return out.charAt(0).toUpperCase() + out.slice(1);
  } catch {
    /* fall through */
  }
  return code;
}
